import { t } from '../core/i18n.js';
import { escapeHtml } from '../core/utils.js';
import { previewMuted, setPreviewMuted, onPreviewMute } from '../core/preview-sound.js';
import { previewEnabled, trailerEmbedUrl, trailerEventsWork, embeddableTrailerId } from './preview.js';

/* Tráiler en la tarjeta desplegada de las filas por categorías (Películas y Series): un rato después de que la tarjeta se quede abierta
   (ratón encima o foco), el tráiler (mudo) se funde sobre el fondo, con un botón para el sonido. Se retira al salir de la tarjeta, al
   terminar, si falla o pasado un tiempo, y solo hay uno a la vez. Mismas reglas que la ventanita flotante (ajuste de vistas previas,
   sin tele ni Android, y solo si la ficha trae tráiler). */

const START_MS = 1300;
const GIVE_UP_MS = 9000;
const MAX_MS = 45000;

let current = null; // { card, frame, button, timers, onMessage, offMute }

const isActive = (card) => card.isConnected && (card.matches(':hover') || card.classList.contains('is-focused') || card.matches(':focus-within'));

export function stopCardTrailer() {
    if (!current) return;
    const c = current; current = null;
    c.timers.forEach(clearTimeout);
    if (c.offMute) c.offMute();
    window.removeEventListener('message', c.onMessage);
    c.frame.classList.remove('is-playing');
    c.frame.src = 'about:blank';
    if (c.frame.parentNode) c.frame.parentNode.removeChild(c.frame);
    if (c.button.parentNode) c.button.parentNode.removeChild(c.button);
}

function start(card, id) {
    const art = card.querySelector('.ce-art');
    if (!art) return;
    stopCardTrailer();
    const frame = document.createElement('iframe');
    frame.className = 'peek-trailer';
    frame.setAttribute('allow', 'autoplay; encrypted-media');
    frame.setAttribute('tabindex', '-1');
    frame.setAttribute('aria-hidden', 'true');
    frame.title = 'Trailer';
    let muted = previewMuted();
    frame.src = trailerEmbedUrl(id, 0, muted);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'peek-sound';
    button.tabIndex = -1;
    button.hidden = true;
    button.setAttribute('aria-label', t('preview.sound'));
    const icon = () => { button.innerHTML = '<span class="mi">' + (muted ? 'volume_off' : 'volume_up') + '</span>'; };
    icon();
    const command = (func, args = []) => { try { frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), '*'); } catch (e) { /* aún no cargado */ } };
    button.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); setPreviewMuted(!muted); }); // no abre la ficha: solo el sonido
    const c = { card, frame, button, timers: [], onMessage: null, offMute: null };
    c.offMute = onPreviewMute((m) => { muted = m; command(muted ? 'mute' : 'unMute'); if (!muted) command('setVolume', [100]); icon(); });
    c.onMessage = (e) => {
        if (e.source !== frame.contentWindow) return;
        let data = e.data;
        if (typeof data === 'string') { try { data = JSON.parse(data); } catch (err) { return; } }
        if (!data || typeof data !== 'object') return;
        if (data.event === 'onError') { stopCardTrailer(); return; }
        const info = data.info;
        const state = data.event === 'onStateChange' ? info : info && typeof info === 'object' ? info.playerState : undefined;
        if (state === 1 && !frame.classList.contains('is-playing')) {
            c.timers.forEach(clearTimeout);
            c.timers = [setTimeout(stopCardTrailer, MAX_MS)];
            frame.classList.add('is-playing');
            button.hidden = false;
            command('unloadModule', ['captions']);
        } else if (state === 0) stopCardTrailer(); // acabó: vuelve la imagen
    };
    window.addEventListener('message', c.onMessage);
    frame.addEventListener('load', () => {
        try { frame.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), '*'); } catch (err) { /* nada */ }
        command('addEventListener', ['onStateChange']);
        command('unloadModule', ['captions']);
    });
    c.timers.push(setTimeout(stopCardTrailer, GIVE_UP_MS));
    current = c;
    art.insertBefore(frame, art.firstChild);
    art.appendChild(button);
}

/** Programa el tráiler de una tarjeta desplegada (si la ficha lo trae y se puede incrustar). */
export function scheduleCardTrailer(card, info) {
    clearTimeout(card._ctTimer);
    if (!previewEnabled() || !trailerEventsWork()) return;
    const id = embeddableTrailerId(info);
    if (!id) return;
    card._ctTimer = setTimeout(() => { if (isActive(card) && !(current && current.card === card)) start(card, id); }, START_MS);
}

/** La tarjeta se cierra (sale el ratón o el foco): se anula lo pendiente y se retira el tráiler de esa tarjeta. */
export function cancelCardTrailer(card) {
    clearTimeout(card._ctTimer);
    if (current && current.card === card) stopCardTrailer();
}
